import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tag,
  Text,
  Tabs,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { SupplierInquiry } from '@texas/api/endpoints/inquiryApi';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { defaultPageSize } from '@texas/components/shared/dataTable/defaultTableOptions';
import { Icons } from '@texas/components/shared/Icons';
import { BlueDot } from '@texas/components/shared/tracker/BlueDot';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { Locale } from '@texas/i18n/types';
import { fadeInAnimation } from '@texas/resources/animations/animations';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { useMemo, useState } from 'react';
import { InquiriesWidgetContext } from './InquiriesWidget';

interface InquiriesTableProps {
  inquiries: SupplierInquiry[];
  tabKey: string;
}

const columnHelper = createColumnHelper<SupplierInquiry>();

const InquiriesTable = ({ inquiries, tabKey }: InquiriesTableProps) => {
  const { error, loading, widgetId } = useActiveContext(InquiriesWidgetContext);
  const [page, setPage] = useState<number>(1);

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('id', {
        cell: (info) => (
          <BlueDot show={info.row.original.isChangedSinceLastTrack}>
            <span />
          </BlueDot>
        ),
        header: '',
        enableSorting: false,
        maxSize: 40,
      }),
      columnHelper.accessor('articleItemCode', {
        cell: (info) => (
          <AngularNavLink
            to="variant.versions"
            navobj={{
              articleId: info.row.original.articleId,
              variantId: info.row.original.variantId,
            }}
          >
            {info.getValue() ?? '—'}
          </AngularNavLink>
        ),
        header: 'Article #',
        meta: { toggleable: true, defaultVisible: true },
      }),
      columnHelper.accessor('formattedInquiryNumber', {
        cell: (info) => (
          <AngularNavLink
            to="variant.inquiry.detail"
            navobj={{
              articleId: info.row.original.articleId,
              variantId: info.row.original.variantId,
              compositionGroupId:
                info.row.original.compositionGroupPurchaseInfoId,
              inquiryId: info.row.original.id,
            }}
          >
            {info.getValue()}
          </AngularNavLink>
        ),
        header: 'Inquiry #',
        meta: { toggleable: true, defaultVisible: true },
      }),
      columnHelper.accessor('supplierName', {
        cell: (info) => info.getValue(),
        header: 'Supplier',
        meta: { toggleable: true, defaultVisible: true },
      }),
      columnHelper.accessor('supplierContactName', {
        cell: (info) => info.getValue() ?? '—',
        header: 'Contact',
        meta: { toggleable: true, defaultVisible: true },
      }),
      columnHelper.accessor('internalOwnerName', {
        cell: (info) => info.getValue() ?? '—',
        header: 'Internal Owner',
        meta: { toggleable: true, defaultVisible: true },
      }),
      columnHelper.accessor('branchIdentifier', {
        cell: (info) => info.getValue(),
        header: 'Branch',
        meta: { toggleable: true, defaultVisible: true },
      }),
      columnHelper.accessor('tierPrices', {
        cell: (info) => (
          <Flex gap={1} wrap="wrap">
            {info.getValue().map((tier) => (
              <Tag
                key={tier.id}
                size="sm"
                colorScheme={tier.isMain ? 'blue' : 'gray'}
                whiteSpace="nowrap"
              >
                {tier.isMain && <Icons.Flag style={{ marginRight: '4px' }} />}
                {tier.quantity}: {tier.price ?? '—'}
              </Tag>
            ))}
          </Flex>
        ),
        header: 'Tiers',
        enableSorting: false,
        meta: { toggleable: true, defaultVisible: true },
      }),
    ];

    return [
      ...baseColumns,
      columnHelper.accessor('acceptedDate', {
        cell: (info) =>
          info.getValue() ? formatDate(Locale.En, info.getValue()!) : '—',
        header: 'Accepted',
        meta: { toggleable: true, defaultVisible: true },
      }),
      columnHelper.accessor('created', {
        cell: (info) => <Text>{formatDate(Locale.En, info.getValue())}</Text>,
        header: 'Created',
        meta: { toggleable: true, defaultVisible: true },
      }),
    ];
  }, []);

  const pagedInquiries = useMemo(() => {
    const offset = (page - 1) * defaultPageSize;
    return inquiries.slice(offset, offset + defaultPageSize);
  }, [inquiries, page]);

  return (
    <DataTableContainer
      w="100%"
      minH={0}
      display="flex"
      flexDir="column"
      error={error}
      css={{ ' th, td': { whiteSpace: 'normal' } }}
      tableId={`inquiries-${tabKey}-${widgetId}`}
      datatable={{
        data: pagedInquiries,
        columns,
        isLoading: loading,
        rowProps: {
          getRowId: (originalRow) => String(originalRow.id),
        },
      }}
      pagination={{
        totalItems: inquiries.length,
        pageSize: defaultPageSize,
        currentPage: page,
        onPageChange: (nextPage) => setPage(nextPage),
      }}
    />
  );
};

export function TableView() {
  const { data } = useActiveContext(InquiriesWidgetContext);

  const open = data.filter((i: SupplierInquiry) => i.sent === null);
  const sent = data.filter(
    (i: SupplierInquiry) => i.sent !== null && i.returned === null,
  );
  const underReview = data.filter((i: SupplierInquiry) => i.returned !== null);

  const tabs = [
    { key: 'open', label: 'Open', inquiries: open },
    { key: 'sent', label: 'Sent', inquiries: sent },
    { key: 'under-review', label: 'Under Review', inquiries: underReview },
  ];

  return (
    <Tabs isLazy={true}>
      <TabList>
        {tabs.map((tab) => {
          const hasChanges = tab.inquiries.some(
            (i: SupplierInquiry) => i.isChangedSinceLastTrack,
          );
          return (
            <Tab key={tab.label}>
              <BlueDot show={hasChanges}>
                <Flex gap={1} align="center">
                  <Tag>{tab.inquiries.length}</Tag>
                  <Text>{tab.label}</Text>
                </Flex>
              </BlueDot>
            </Tab>
          );
        })}
      </TabList>
      <TabPanels>
        {tabs.map((tab) => (
          <TabPanel key={tab.label}>
            <Box animation={fadeInAnimation()}>
              <InquiriesTable inquiries={tab.inquiries} tabKey={tab.key} />
            </Box>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
}
